import { useState, useEffect, useCallback, useRef } from 'react';
import { getContract, JSONRpcProvider } from 'opnet';
import { networks } from '@btc-vision/bitcoin';
import { Campaign, CATEGORIES } from '../config/campaigns';
import { DonationRecord } from '../config/donations';
import { BITFUND_CONTRACT_ADDRESS, BITFUND_ABI, IBitFundContract } from '../config/contract';

const SATS_PER_BTC = 100_000_000;
const RPC_URL = 'https://testnet.opnet.org';

function satsToBtc(sats: bigint): number {
    return Number(sats) / SATS_PER_BTC;
}

export interface BitFundData {
    readonly campaigns: Campaign[];
    readonly donations: DonationRecord[];
    readonly totalRaisedBTC: number;
    readonly loading: boolean;
    readonly refresh: () => Promise<void>;
}

export function useBitFund(): BitFundData {
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [donations, setDonations] = useState<DonationRecord[]>([]);
    const [totalRaisedBTC, setTotalRaisedBTC] = useState(0);
    const [loading, setLoading] = useState(true);
    const rpcRef = useRef<JSONRpcProvider | null>(null);

    const fetchData = useCallback(async () => {
        try {
            const network = networks.opnetTestnet;

            // Create a standalone RPC provider for read-only calls (no wallet needed)
            if (!rpcRef.current) {
                rpcRef.current = new JSONRpcProvider({ url: RPC_URL, network });
            }
            const rpcProvider = rpcRef.current;

            const contract = getContract<IBitFundContract>(
                BITFUND_CONTRACT_ADDRESS,
                BITFUND_ABI,
                rpcProvider,
                network,
            );

            // Fetch campaign count
            const countResult = await contract.getCampaignCount();
            const countProps = countResult.properties as Record<string, unknown>;
            const campaignCount = Number(countProps.count ?? 0n);

            // Fetch all campaigns
            const loadedCampaigns: Campaign[] = [];
            for (let i = 1; i <= campaignCount; i++) {
                try {
                    const result = await contract.getCampaign(BigInt(i));
                    const p = result.properties as Record<string, unknown>;

                    const categoryIdx = Number(p.categoryIndex ?? 0);
                    const cat = CATEGORIES[categoryIdx] ?? CATEGORIES[0];

                    // Creator stored as u256 — convert to hex string
                    const creatorU256 = (p.creator as bigint) ?? 0n;
                    const creatorHex =
                        '0x' + creatorU256.toString(16).padStart(64, '0');

                    loadedCampaigns.push({
                        id: BigInt(i),
                        title: (p.title as string) ?? '',
                        description: (p.description as string) ?? '',
                        metadata: (p.metadata as string) ?? '',
                        category: cat.value,
                        icon: cat.icon,
                        goalBTC: satsToBtc((p.goal as bigint) ?? 0n),
                        raisedBTC: satsToBtc((p.raised as bigint) ?? 0n),
                        backers: Number(p.backers ?? 0n),
                        creatorAddress: creatorHex,
                        createdAtBlock: (p.createdAtBlock as bigint) ?? 0n,
                        durationDays: Number(p.durationDays ?? 0),
                        active: (p.active as boolean) ?? false,
                    });
                } catch (err) {
                    console.error(`Failed to load campaign ${i}:`, err);
                }
            }

            setCampaigns(loadedCampaigns);

            // Fetch total raised from contract (single authoritative value)
            const totalResult = await contract.totalDonations();
            const totalProps = totalResult.properties as Record<string, unknown>;
            setTotalRaisedBTC(satsToBtc((totalProps.total as bigint) ?? 0n));

            // Fetch donation count
            const donCountResult = await contract.getDonationCount();
            const donCountProps = donCountResult.properties as Record<string, unknown>;
            const donationCount = Number(donCountProps.count ?? 0n);

            // Fetch all donations
            const loadedDonations: DonationRecord[] = [];
            for (let i = 1; i <= donationCount; i++) {
                try {
                    const result = await contract.getDonation(BigInt(i));
                    const p = result.properties as Record<string, unknown>;

                    const donorU256 = (p.donor as bigint) ?? 0n;
                    const donorHex =
                        '0x' + donorU256.toString(16).padStart(64, '0');

                    const txHashU256 = (p.txHash as bigint) ?? 0n;
                    const txId = txHashU256 !== 0n
                        ? txHashU256.toString(16).padStart(64, '0')
                        : undefined;

                    loadedDonations.push({
                        id: BigInt(i),
                        campaignId: (p.campaignId as bigint) ?? 0n,
                        donorAddress: donorHex,
                        amountBTC: satsToBtc((p.amount as bigint) ?? 0n),
                        blockHeight: (p.blockHeight as bigint) ?? 0n,
                        txId,
                    });
                } catch (err) {
                    console.error(`Failed to load donation ${i}:`, err);
                }
            }

            setDonations(loadedDonations);
        } catch (err) {
            console.error('Failed to fetch BitFund data:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void fetchData();

        // Auto-refresh every 30 seconds to pick up new blocks
        const interval = setInterval(() => {
            void fetchData();
        }, 30_000);

        return () => clearInterval(interval);
    }, [fetchData]);

    return { campaigns, donations, totalRaisedBTC, loading, refresh: fetchData };
}
